import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  deleteSandboxSkillDocument,
  downloadSandboxSkillDocument,
  getSandboxSkillDocuments,
  getSandboxSkills,
  updateSandboxSkill,
  uploadSandboxSkillDocument,
} from "@/utils/apiCalls";
import { SandboxSkill, SandboxTemplate } from "@/types";
import { useAuthInfo } from "@propelauth/react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { marked } from "marked";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import TurndownService from "turndown";

const SAVE_DELAY_MS = 900;
const turndownService = new TurndownService({
  bulletListMarker: "-",
  codeBlockStyle: "fenced",
  headingStyle: "atx",
});

const skillKey = (skill: SandboxSkill) =>
  [
    skill.name,
    skill.scope,
    skill.specialty ?? "",
    skill.output_document_type_id ?? "",
    skill.section_id ?? "",
    skill.clinician_id ?? "",
  ].join(":");

const latestSkillVersions = (skills: SandboxSkill[]) => {
  const latest = new Map<string, SandboxSkill>();
  for (const skill of skills) {
    const key = skillKey(skill);
    const current = latest.get(key);
    if (!current || skill.version > current.version) {
      latest.set(key, skill);
    }
  }
  return Array.from(latest.values()).sort((a, b) =>
    a.name.localeCompare(b.name)
  );
};

const markdownTextToEditorHtml = (content: string) => {
  if (!content.trim()) {
    return "<p></p>";
  }
  return marked.parse(content, { async: false }) as string;
};

const editorHtmlToMarkdown = (html: string) => {
  return turndownService.turndown(html).trim();
};

const formatSkillName = (skill: SandboxSkill) =>
  skill.name.replace(/[_-]/g, " ");

const formatStorageName = (storagePath: string) =>
  storagePath.split("/").pop() ?? storagePath;

const formatDocumentPath = (document: SandboxTemplate) => {
  const workspacePath = document.template_metadata?.workspace_path;
  if (typeof workspacePath === "string") {
    return workspacePath;
  }
  return formatStorageName(document.storage_path);
};

export const SandboxConfigPage = () => {
  const authInfo = useAuthInfo();
  const navigate = useNavigate();
  const location = useLocation();
  const { skillId } = useParams();
  const [skills, setSkills] = useState<SandboxSkill[]>([]);
  const [documentsBySkillId, setDocumentsBySkillId] = useState<
    Record<string, SandboxTemplate[]>
  >({});
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [saveState, setSaveState] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const [isUploading, setIsUploading] = useState(false);
  const selectedSkillIdRef = useRef<string | null>(null);
  const lastSavedContentRef = useRef<string>("");
  const saveTimeoutRef = useRef<number | null>(null);
  const accessTokenRef = useRef<string | null>(authInfo.accessToken ?? null);

  const refresh = async () => {
    const skillResponse = await getSandboxSkills(authInfo.accessToken ?? null);
    setSkills(skillResponse ?? []);
  };

  useEffect(() => {
    accessTokenRef.current = authInfo.accessToken ?? null;
    refresh();
  }, [authInfo.accessToken]);

  const visibleSkills = useMemo(() => latestSkillVersions(skills), [skills]);
  const selectedSkill = useMemo(
    () =>
      visibleSkills.find((skill) => skill.id === skillId) ??
      visibleSkills[0] ??
      null,
    [skillId, visibleSkills]
  );
  const selectedDocuments = selectedSkill
    ? documentsBySkillId[selectedSkill.id] ?? []
    : [];
  const routeBase = location.pathname.startsWith("/physician")
    ? "/physician/sandbox-skills"
    : "/admin/sandbox-config";

  const editor = useEditor({
    extensions: [StarterKit],
    content: markdownTextToEditorHtml(""),
    editorProps: {
      attributes: {
        class:
          "min-h-[520px] max-w-none p-4 text-sm leading-6 outline-none prose prose-sm",
      },
    },
    onUpdate: ({ editor }) => {
      const selectedId = selectedSkillIdRef.current;
      if (!selectedId) {
        return;
      }
      const content = editorHtmlToMarkdown(editor.getHTML());
      if (content === lastSavedContentRef.current) {
        setSaveState("saved");
        return;
      }
      if (saveTimeoutRef.current) {
        window.clearTimeout(saveTimeoutRef.current);
      }
      setSaveState("saving");
      saveTimeoutRef.current = window.setTimeout(async () => {
        const response = await updateSandboxSkill(
          selectedId,
          { content },
          accessTokenRef.current
        );
        if (!response) {
          setSaveState("error");
          toast.error("Failed to autosave skill");
          return;
        }
        lastSavedContentRef.current = response.content;
        setSkills((current) =>
          current.map((skill) => (skill.id === response.id ? response : skill))
        );
        setSaveState("saved");
      }, SAVE_DELAY_MS);
    },
  });

  const loadSkillDocuments = async (targetSkillId: string) => {
    const response = await getSandboxSkillDocuments(
      targetSkillId,
      authInfo.accessToken ?? null
    );
    setDocumentsBySkillId((current) => ({
      ...current,
      [targetSkillId]: response ?? [],
    }));
  };

  useEffect(() => {
    if (!skillId && visibleSkills.length > 0) {
      navigate(`${routeBase}/${visibleSkills[0].id}`, { replace: true });
    }
  }, [navigate, routeBase, skillId, visibleSkills]);

  useEffect(() => {
    if (!selectedSkill || !editor) {
      return;
    }
    if (saveTimeoutRef.current) {
      window.clearTimeout(saveTimeoutRef.current);
    }
    selectedSkillIdRef.current = selectedSkill.id;
    lastSavedContentRef.current = selectedSkill.content;
    setSaveState("saved");
    editor.commands.setContent(
      markdownTextToEditorHtml(selectedSkill.content),
      { emitUpdate: false }
    );
    loadSkillDocuments(selectedSkill.id);
  }, [editor, selectedSkill?.id, authInfo.accessToken]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        window.clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const uploadDocument = async () => {
    if (!selectedSkill || !documentFile) {
      toast.error("Choose a DOCX file to upload");
      return;
    }
    setIsUploading(true);
    const response = await uploadSandboxSkillDocument(
      {
        skillId: selectedSkill.id,
        file: documentFile,
      },
      authInfo.accessToken ?? null
    );
    setIsUploading(false);
    if (!response) {
      toast.error("Failed to upload skill document");
      return;
    }
    toast.success("Skill document uploaded");
    setDocumentFile(null);
    await loadSkillDocuments(selectedSkill.id);
  };

  const downloadDocument = async (documentId: string) => {
    if (!selectedSkill) {
      return;
    }
    const response = await downloadSandboxSkillDocument(
      selectedSkill.id,
      documentId,
      authInfo.accessToken ?? null
    );
    if (!response) {
      toast.error("Failed to download skill document");
    }
  };

  const deleteDocument = async (documentId: string) => {
    if (!selectedSkill) {
      return;
    }
    const confirmed = window.confirm("Delete this skill document?");
    if (!confirmed) {
      return;
    }
    const success = await deleteSandboxSkillDocument(
      selectedSkill.id,
      documentId,
      authInfo.accessToken ?? null
    );
    if (!success) {
      toast.error("Failed to delete skill document");
      return;
    }
    toast.success("Skill document deleted");
    await loadSkillDocuments(selectedSkill.id);
  };

  return (
    <Layout title="Skills">
      <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
        <aside className="space-y-2 rounded border p-3">
          <div className="text-sm font-medium">Skills</div>
          {visibleSkills.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No skills have been created yet.
            </p>
          )}
          {visibleSkills.map((skill) => (
            <a
              key={skill.id}
              href={`${routeBase}/${skill.id}`}
              className={`block rounded px-3 py-2 text-sm ${
                selectedSkill?.id === skill.id
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-muted"
              }`}
            >
              <div className="font-medium capitalize">
                {formatSkillName(skill)}
              </div>
              <div className="text-xs opacity-80">{skill.scope}</div>
            </a>
          ))}
        </aside>

        {selectedSkill ? (
          <section className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold capitalize">
                  {formatSkillName(selectedSkill)}
                </h2>
                <div className="text-sm text-muted-foreground">
                  {selectedSkill.scope}
                  {selectedSkill.specialty
                    ? ` · ${selectedSkill.specialty}`
                    : ""}
                  {selectedSkill.output_document_type_id
                    ? ` · ${selectedSkill.output_document_type_id}`
                    : ""}
                </div>
              </div>
              <span
                className={`text-sm ${
                  saveState === "error"
                    ? "text-destructive"
                    : "text-muted-foreground"
                }`}
              >
                {saveState === "saving"
                  ? "Saving..."
                  : saveState === "error"
                    ? "Autosave failed"
                    : "Saved"}
              </span>
            </div>

            <div className="overflow-hidden rounded border bg-background">
              <EditorContent editor={editor} />
            </div>

            <section className="space-y-3 rounded border p-4">
              <div>
                <h3 className="font-semibold">Skill Documents</h3>
                <p className="text-sm text-muted-foreground">
                  Upload files that belong to this skill, including templates
                  and reference documents.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Input
                  className="max-w-md"
                  type="file"
                  onChange={(e) => setDocumentFile(e.target.files?.[0] ?? null)}
                />
                <Button
                  onClick={uploadDocument}
                  disabled={!documentFile || isUploading}
                >
                  {isUploading ? "Uploading..." : "Upload Document"}
                </Button>
              </div>
              <div className="space-y-2">
                {selectedDocuments.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    No documents uploaded for this skill.
                  </p>
                )}
                {selectedDocuments.map((document) => (
                  <div
                    key={document.id}
                    className="flex items-center justify-between rounded border p-3"
                  >
                    <div>
                      <div className="font-medium">{document.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {formatDocumentPath(document)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        onClick={() => downloadDocument(document.id)}
                      >
                        Download
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => deleteDocument(document.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </section>
        ) : (
          <section className="rounded border p-4 text-sm text-muted-foreground">
            Select a skill from Admin Settings once skills have been created in
            the database.
          </section>
        )}
      </div>
    </Layout>
  );
};
